import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { PriceListEditor, PriceListModal } from "@/components/PriceListEditor";
import { AgentAccountCard } from "@/components/AgentAccountCard";

const CATS = [
  { v: "incollare", l: "Incolla / Sigilla" },
  { v: "lubrificare", l: "Lubrifica" },
  { v: "pulire", l: "Pulisci" },
  { v: "protettivi", l: "Proteggi" },
  { v: "nastri_fascette", l: "Nastri e fascette" },
  { v: "guaine", l: "Guaine" },
  { v: "attrezzatura", l: "Attrezzatura" },
  { v: "altri_prodotti", l: "Altri prodotti" },
];
const CAT_LABEL = Object.fromEntries(CATS.map((c) => [c.v, c.l]));

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products"); // products | requests | prices | wizard
  const [priceProduct, setPriceProduct] = useState(null);
  const [priceCounts, setPriceCounts] = useState({ by_product: {}, standalone: 0 });
  const loadPriceCounts = () => {
    api.get("/admin/prices/counts").then((r) => setPriceCounts(r.data)).catch(() => {});
  };
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [authLoading, user, navigate]);

  const loadProducts = () => {
    setLoading(true);
    api.get("/products" + (filter ? `?category=${filter}` : ""))
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  };

  const loadRequests = () => {
    setLoading(true);
    api.get("/scheda-requests")
      .then((r) => setRequests(r.data))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  const loadPendingCount = () => {
    api.get("/scheda-requests/pending-count")
      .then((r) => setPendingCount(r.data.pending || 0))
      .catch(() => setPendingCount(0));
  };

  useEffect(() => {
    if (!user) return;
    loadPendingCount();
    if (tab === "products") { loadProducts(); loadPriceCounts(); }
    if (tab === "requests") loadRequests();
     
  }, [user, tab, filter]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/products/upload-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Prodotto estratto: ${r.data.name}`);
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'estrazione");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const [editCategoriesProduct, setEditCategoriesProduct] = useState(null);
  const [editImageProduct, setEditImageProduct] = useState(null);
  const [editNameProduct, setEditNameProduct] = useState(null);
  const [editDataProduct, setEditDataProduct] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // {title, message, onConfirm}

  const remove = (product) => {
    setConfirmDialog({
      title: "Eliminare il prodotto?",
      message: `"${product.name}" sarà rimosso dal catalogo. Questa operazione non è reversibile.`,
      confirmLabel: "Sì, elimina",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/products/${product.id}`);
          toast.success("Prodotto eliminato");
          loadProducts();
        } catch {
          toast.error("Errore durante l'eliminazione");
        }
      },
    });
  };

  const editImage = (product) => setEditImageProduct(product);

  const saveImageUrl = async (product, url) => {
    try {
      await api.patch(`/products/${product.id}/image`, { image_url: url || null });
      toast.success("Immagine aggiornata");
      setEditImageProduct(null);
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento immagine");
    }
  };

  const exportCatalog = async () => {
    try {
      const r = await api.get("/catalog/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `top-tec-catalog-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Backup scaricato: ${r.data.count} prodotti`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore export");
    }
  };

  const importCatalog = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setConfirmDialog({
      title: "Importare il backup?",
      message: `File: "${file.name}"\n\nModalità MERGE: aggiunge solo i prodotti non ancora presenti (per ID). Non tocca i tuoi prodotti attuali.`,
      confirmLabel: "Sì, importa",
      danger: false,
      onCancel: () => { e.target.value = ""; },
      onConfirm: async () => {
        try {
          const text = await file.text();
          const data = JSON.parse(text);
          const products = Array.isArray(data) ? data : (data.products || []);
          const r = await api.post("/catalog/import", { products, mode: "merge" });
          toast.success(`Import: +${r.data.inserted} nuovi, ${r.data.skipped} già presenti`);
          loadProducts();
        } catch (err) {
          toast.error(err.response?.data?.detail || err.message || "Errore import");
        } finally {
          e.target.value = "";
        }
      },
    });
  };

  const markProcessed = async (id, currentStatus) => {
    const newStatus = currentStatus === "processed" ? "pending" : "processed";
    try {
      await api.patch(`/scheda-requests/${id}?status=${newStatus}`);
      toast.success(newStatus === "processed" ? "Segnata come inviata" : "Riportata a pendente");
      loadRequests();
      loadPendingCount();
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const removeRequest = (req) => {
    setConfirmDialog({
      title: "Eliminare la richiesta?",
      message: `Rimuoverà la richiesta di ${req.client_email} per "${req.product_name}". Non è reversibile.`,
      confirmLabel: "Sì, elimina",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/scheda-requests/${req.id}`);
          loadRequests();
          loadPendingCount();
        } catch {
          toast.error("Errore eliminazione");
        }
      },
    });
  };

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen tm-grid-bg">
      <Header variant="admin" />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="tm-tag inline-block mb-3">Admin · Dashboard</div>
            <h1 className="font-heading text-3xl md:text-5xl text-white tracking-tighter" data-testid="admin-title">
              Pannello di controllo
            </h1>
            <p className="text-slate-400 mt-3">
              Gestisci schede tecniche e richieste dei clienti.
            </p>
          </div>

          {tab === "products" && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={exportCatalog}
                className="tm-btn-outline text-sm"
                data-testid="export-catalog-btn"
                title="Scarica un backup JSON del catalogo prodotti"
              >
                ⇩ Esporta backup
              </button>
              <label className="tm-btn-outline text-sm cursor-pointer" data-testid="import-catalog-label">
                <input
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={importCatalog}
                  data-testid="import-catalog-input"
                />
                ⇪ Importa backup
              </label>
              <label className="tm-btn-primary cursor-pointer" data-testid="upload-pdf-btn">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={onUpload}
                  disabled={uploading}
                  data-testid="upload-pdf-input"
                />
                {uploading ? "Estrazione..." : "+ Carica PDF"}
              </label>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-slate-700">
          <button
            onClick={() => setTab("products")}
            className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 transition-colors ${
              tab === "products" ? "border-orange-500 text-orange-500" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
            data-testid="tab-products"
          >
            📦 Catalogo prodotti
          </button>
          <button
            onClick={() => setTab("requests")}
            className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 transition-colors relative ${
              tab === "requests" ? "border-orange-500 text-orange-500" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
            data-testid="tab-requests"
          >
            📩 Richieste schede
            {pendingCount > 0 && (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold"
                data-testid="pending-badge"
              >
                {pendingCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("prices")}
            className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 transition-colors ${
              tab === "prices" ? "border-orange-500 text-orange-500" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
            data-testid="tab-prices"
          >
            € Listini extra
          </button>
          <button
            onClick={() => setTab("agent")}
            className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 transition-colors ${
              tab === "agent" ? "border-orange-500 text-orange-500" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
            data-testid="tab-agent"
          >
            🔑 Agente
          </button>
          <button
            onClick={() => setTab("wizard")}
            className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 transition-colors ${
              tab === "wizard" ? "border-orange-500 text-orange-500" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
            data-testid="tab-wizard"
          >
            ⚙️ Opzioni Wizard
          </button>
        </div>

        {tab === "products" && (
          <>
            <div className="flex gap-2 mb-6 flex-wrap">
              <button
                onClick={() => setFilter("")}
                className={`tm-btn-outline text-sm ${filter === "" ? "border-orange-500 text-orange-500" : ""}`}
                data-testid="filter-all"
              >
                Tutti ({products.length})
              </button>
              {CATS.map((c) => (
                <button
                  key={c.v}
                  onClick={() => setFilter(c.v)}
                  className={`tm-btn-outline text-sm ${filter === c.v ? "border-orange-500 text-orange-500" : ""}`}
                  data-testid={`filter-${c.v}`}
                >
                  {c.l}
                </button>
              ))}
            </div>

            <div className="tm-card overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/80 border-b border-slate-700">
                  <tr className="text-left font-mono text-xs uppercase tracking-widest text-slate-400">
                    <th className="p-4">Foto</th>
                    <th className="p-4">Nome</th>
                    <th className="p-4">Categoria</th>
                    <th className="p-4 hidden md:table-cell">Materiali</th>
                    <th className="p-4 hidden lg:table-cell">Temp (°C)</th>
                    <th className="p-4 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500 font-mono uppercase tracking-widest text-sm">
                        Caricamento...
                      </td>
                    </tr>
                  )}
                  {!loading && products.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-12 text-center text-slate-400" data-testid="admin-empty">
                        Nessun prodotto. Carica il tuo primo PDF per iniziare.
                      </td>
                    </tr>
                  )}
                  {products.map((p) => {
                    const cats = (p.categories && p.categories.length > 0) ? p.categories : [p.category];
                    return (
                    <tr key={p.id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`row-${p.id}`}>
                      <td className="p-4">
                        <button
                          onClick={() => editImage(p)}
                          className="w-14 h-14 border border-slate-700 hover:border-orange-500 transition-colors bg-slate-900 flex items-center justify-center overflow-hidden"
                          title="Modifica immagine"
                          data-testid={`edit-image-${p.id}`}
                        >
                          {p.image_url ? (
                            <img src={p.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          ) : (
                            <span className="text-2xl text-slate-600">+</span>
                          )}
                        </button>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => setEditNameProduct(p)}
                          className="text-left group"
                          title="Modifica nome / brand"
                          data-testid={`edit-name-${p.id}`}
                        >
                          <div className="text-white font-medium group-hover:text-orange-400 transition-colors">
                            {p.name} <span className="text-slate-500 text-xs group-hover:text-orange-400">✎</span>
                          </div>
                          {p.brand && <div className="text-slate-500 text-xs">{p.brand}</div>}
                        </button>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => setEditCategoriesProduct(p)}
                          className="flex flex-wrap gap-1 items-center hover:opacity-80 transition-opacity"
                          title="Modifica categorie"
                          data-testid={`edit-categories-${p.id}`}
                        >
                          {cats.map((c) => (
                            <span key={c} className="tm-tag text-orange-500 border-orange-800">{CAT_LABEL[c] || c}</span>
                          ))}
                          <span className="text-slate-500 text-xs ml-1">✎</span>
                        </button>
                      </td>
                      <td className="p-4 hidden md:table-cell text-slate-300 text-sm">
                        <button
                          onClick={() => setEditDataProduct(p)}
                          className="text-left hover:text-orange-400 transition-colors"
                          title="Modifica materiali / temperatura"
                          data-testid={`edit-data-mat-${p.id}`}
                        >
                          {(p.materials_admin && p.materials_admin.length > 0) ? (
                            <>
                              <span className="text-orange-400">{p.materials_admin.slice(0, 3).join(", ")}</span>
                              {p.materials_admin.length > 3 && "…"}
                              <span className="ml-1 text-orange-500 text-xs">✎ admin</span>
                            </>
                          ) : (
                            <>
                              {(p.materials || []).slice(0, 3).join(", ") || <span className="text-slate-600 italic">—</span>}
                              {(p.materials || []).length > 3 && "…"}
                              <span className="ml-1 text-slate-600 text-xs">✎</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="p-4 hidden lg:table-cell text-slate-300 font-mono text-sm">
                        <button
                          onClick={() => setEditDataProduct(p)}
                          className="text-left hover:text-orange-400 transition-colors"
                          title="Modifica materiali / temperatura"
                          data-testid={`edit-data-temp-${p.id}`}
                        >
                          {(p.temperature_min_admin != null || p.temperature_max_admin != null) ? (
                            <span className="text-orange-400">
                              {p.temperature_min_admin ?? "?"} — {p.temperature_max_admin ?? "?"}
                              <span className="ml-1 text-orange-500 text-xs">✎</span>
                            </span>
                          ) : (
                            <>
                              {p.temperature_min ?? "?"} — {p.temperature_max ?? "?"}
                              <span className="ml-1 text-slate-600 text-xs">✎</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex flex-col items-end gap-2">
                        <button
                          onClick={() => setPriceProduct(p)}
                          className="text-orange-400 hover:text-orange-300 font-mono text-xs uppercase tracking-widest whitespace-nowrap"
                          data-testid={`prices-${p.id}`}
                        >
                          € Listino ({priceCounts.by_product[p.id] || 0})
                        </button>
                        <button
                          onClick={() => remove(p)}
                          className="text-red-400 hover:text-red-300 font-mono text-xs uppercase tracking-widest"
                          data-testid={`delete-${p.id}`}
                        >
                          Elimina
                        </button>
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "requests" && (
          <div className="space-y-6">
            <AccessRequestsPanel />
            <div className="tm-card overflow-x-auto"><table className="w-full">
              <thead className="bg-slate-800/80 border-b border-slate-700">
                <tr className="text-left font-mono text-xs uppercase tracking-widest text-slate-400">
                  <th className="p-4">Stato</th>
                  <th className="p-4">Data</th>
                  <th className="p-4">Cliente</th>
                  <th className="p-4">Prodotto</th>
                  <th className="p-4 hidden md:table-cell">Note</th>
                  <th className="p-4 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500 font-mono uppercase tracking-widest text-sm">
                      Caricamento...
                    </td>
                  </tr>
                )}
                {!loading && requests.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-12 text-center text-slate-400" data-testid="requests-empty">
                      Nessuna richiesta ricevuta.
                    </td>
                  </tr>
                )}
                {requests.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-800 hover:bg-slate-800/40 ${r.status === "pending" ? "bg-orange-500/5" : "opacity-60"}`} data-testid={`request-row-${r.id}`}>
                    <td className="p-4">
                      {r.status === "pending" ? (
                        <span className="tm-tag text-orange-500 border-orange-800">● In attesa</span>
                      ) : (
                        <span className="tm-tag text-green-500 border-green-800">✓ Inviata</span>
                      )}
                    </td>
                    <td className="p-4 text-slate-400 font-mono text-xs">
                      {new Date(r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="p-4">
                      <a href={`mailto:${r.client_email}?subject=Scheda tecnica ${r.product_name}`} className="text-orange-400 hover:text-orange-300 underline-offset-2 hover:underline" data-testid={`request-email-${r.id}`}>
                        {r.client_email}
                      </a>
                      {r.client_name && <div className="text-slate-500 text-xs mt-1">{r.client_name}</div>}
                    </td>
                    <td className="p-4">
                      <div className="text-white font-medium">{r.product_name}</div>
                      <div className="text-slate-500 text-xs mt-1 capitalize">{r.product_category}</div>
                    </td>
                    <td className="p-4 hidden md:table-cell text-slate-300 text-sm max-w-xs">
                      {r.message || <span className="text-slate-600 italic">—</span>}
                    </td>
                    <td className="p-4 text-right space-x-3 whitespace-nowrap">
                      <button
                        onClick={() => markProcessed(r.id, r.status)}
                        className="font-mono text-xs uppercase tracking-widest text-orange-500 hover:text-orange-400"
                        data-testid={`mark-processed-${r.id}`}
                      >
                        {r.status === "pending" ? "Segna inviata" : "Riapri"}
                      </button>
                      <button
                        onClick={() => removeRequest(r)}
                        className="font-mono text-xs uppercase tracking-widest text-red-400 hover:text-red-300"
                        data-testid={`delete-request-${r.id}`}
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {tab === "prices" && (
          <div className="tm-card p-6" data-testid="standalone-prices">
            <h3 className="font-heading text-2xl text-white tracking-tighter mb-1">Listini senza prodotto</h3>
            <p className="text-slate-400 text-sm mb-6">
              Prezzi di articoli che non sono nel catalogo. Visibili solo all&apos;admin. I listini dei prodotti eliminati finiscono qui.
            </p>
            <PriceListEditor />
          </div>
        )}

        {tab === "agent" && <AgentAccountCard />}

        {tab === "wizard" && (
          <WizardOptionsEditor />
        )}
      </div>

      {priceProduct && (
        <PriceListModal
          product={priceProduct}
          onClose={() => setPriceProduct(null)}
          onChanged={loadPriceCounts}
        />
      )}

      {editCategoriesProduct && (
        <CategoriesEditModal
          product={editCategoriesProduct}
          onClose={() => setEditCategoriesProduct(null)}
          onSaved={() => {
            setEditCategoriesProduct(null);
            loadProducts();
          }}
        />
      )}

      {editImageProduct && (
        <ImageEditModal
          product={editImageProduct}
          onClose={() => setEditImageProduct(null)}
          onSave={(url) => saveImageUrl(editImageProduct, url)}
        />
      )}

      {editNameProduct && (
        <NameEditModal
          product={editNameProduct}
          onClose={() => setEditNameProduct(null)}
          onSaved={() => {
            setEditNameProduct(null);
            loadProducts();
          }}
        />
      )}

      {editDataProduct && (
        <DataEditModal
          product={editDataProduct}
          onClose={() => setEditDataProduct(null)}
          onSaved={() => {
            setEditDataProduct(null);
            loadProducts();
          }}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          onCancel={() => {
            if (confirmDialog.onCancel) confirmDialog.onCancel();
            setConfirmDialog(null);
          }}
          onConfirm={async () => {
            const fn = confirmDialog.onConfirm;
            setConfirmDialog(null);
            if (fn) await fn();
          }}
        />
      )}
    </div>
  );
}

const CATEGORY_OPTIONS = CATS.map((c) => ({ value: c.v, label: c.l }));

function CategoriesEditModal({ product, onClose, onSaved }) {
  const initialCats = (product.categories && product.categories.length > 0)
    ? product.categories
    : (product.category ? [product.category] : []);
  const [selected, setSelected] = useState(initialCats);
  const [saving, setSaving] = useState(false);

  const toggle = (value) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error("Seleziona almeno una categoria");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}/categories`, { categories: selected });
      toast.success("Categorie aggiornate");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento categorie");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="categories-modal">
      <div className="tm-card p-8 w-full max-w-lg">
        <div className="tm-tag inline-block mb-4">Categorie prodotto</div>
        <h3 className="font-heading text-2xl text-white tracking-tighter mb-2">
          {product.name}
        </h3>
        <p className="text-slate-400 text-sm mb-6">
          Un prodotto può appartenere a più categorie (es. un olio multiuso che lubrifica, pulisce e protegge). Seleziona tutte quelle applicabili.
        </p>

        <div className="grid gap-3 mb-6">
          {CATEGORY_OPTIONS.map((opt) => {
            const active = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => toggle(opt.value)}
                className={`tm-radio-card text-left flex justify-between items-center ${active ? "active" : ""}`}
                data-testid={`cat-option-${opt.value}`}
              >
                <span className="text-white">{opt.label}</span>
                <span className={`w-4 h-4 border ${active ? "border-orange-500 bg-orange-500" : "border-slate-500"}`} />
              </button>
            );
          })}
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="tm-btn-outline flex-1" data-testid="cat-cancel">
            Annulla
          </button>
          <button onClick={save} disabled={saving || selected.length === 0} className="tm-btn-primary flex-1 disabled:opacity-50" data-testid="cat-save">
            {saving ? "Salvo..." : "Salva categorie"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({ title, message, confirmLabel = "Conferma", danger = false, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="confirm-dialog">
      <div className="tm-card p-8 w-full max-w-md">
        <h3 className="font-heading text-2xl text-white tracking-tighter mb-3" data-testid="confirm-title">
          {title}
        </h3>
        <p className="text-slate-300 text-sm mb-6 whitespace-pre-line" data-testid="confirm-message">
          {message}
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="tm-btn-outline flex-1" data-testid="confirm-cancel">
            Annulla
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-6 py-3 font-semibold transition-colors ${
              danger
                ? "bg-red-600 hover:bg-red-700 text-white border border-red-600"
                : "bg-orange-500 hover:bg-orange-600 text-white border border-orange-500"
            }`}
            data-testid="confirm-ok"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImageEditModal({ product, onClose, onSave }) {
  const [url, setUrl] = useState(product.image_url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/products/${product.id}/image-upload`, fd);
      setUrl(r.data.image_url);
      toast.success("Foto caricata nell'archivio permanente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento foto");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(url.trim());
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="image-modal">
      <div className="tm-card p-8 w-full max-w-lg">
        <div className="tm-tag inline-block mb-4">Foto prodotto</div>
        <h3 className="font-heading text-2xl text-white tracking-tighter mb-2">
          {product.name}
        </h3>
        <p className="text-slate-400 text-sm mb-6">
          Carica una foto dal computer oppure incolla l&apos;URL di una foto pubblica. Lascia vuoto per rimuovere.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tm-label">Carica dal computer</label>
            <label className={`tm-btn-outline w-full text-center cursor-pointer block ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="image-file-label">
              {uploading ? "Caricamento..." : "Scegli file (JPG, PNG, WEBP · max 10 MB)"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={uploadFile}
                data-testid="image-file-input"
              />
            </label>
          </div>
          <div>
            <label className="tm-label">URL immagine</label>
            <input
              type="text"
              className="tm-input"
              placeholder="https://..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
              data-testid="image-url-input"
            />
          </div>

          {url && (
            <div className="border border-slate-700 bg-slate-900 aspect-video overflow-hidden">
              <img
                src={url}
                alt="Anteprima"
                className="w-full h-full object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="image-preview"
              />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="tm-btn-outline flex-1" data-testid="image-cancel">
              Annulla
            </button>
            <button type="submit" disabled={saving} className="tm-btn-primary flex-1 disabled:opacity-50" data-testid="image-save">
              {saving ? "Salvo..." : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function NameEditModal({ product, onClose, onSaved }) {
  const [name, setName] = useState(product.name || "");
  const [brand, setBrand] = useState(product.brand || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Il nome non può essere vuoto");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}/name`, {
        name: trimmed,
        brand: brand.trim() || null,
      });
      toast.success("Nome aggiornato");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento nome");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="name-modal">
      <div className="tm-card p-8 w-full max-w-lg">
        <div className="tm-tag inline-block mb-4">Modifica nome prodotto</div>
        <h3 className="font-heading text-2xl text-white tracking-tighter mb-2">
          {product.name}
        </h3>
        <p className="text-slate-400 text-sm mb-6">
          Correggi il nome o il brand del prodotto se l&apos;estrazione automatica ha commesso un errore.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tm-label">Nome prodotto *</label>
            <input
              type="text"
              className="tm-input"
              placeholder="es. ZINKTOP SPRAY"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              data-testid="name-input"
            />
          </div>

          <div>
            <label className="tm-label">Brand (opzionale)</label>
            <input
              type="text"
              className="tm-input"
              placeholder="es. TOP-TEC"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              data-testid="brand-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="tm-btn-outline flex-1" data-testid="name-cancel">
              Annulla
            </button>
            <button type="submit" disabled={saving} className="tm-btn-primary flex-1 disabled:opacity-50" data-testid="name-save">
              {saving ? "Salvo..." : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const COMMON_MATERIALS = [
  "Acciaio", "Alluminio", "Ferro", "Rame", "Ottone", "Zinco", "Leghe",
  "Plastica", "PVC", "PP", "PE", "ABS", "Nylon", "Gomma", "EPDM",
  "Vetro", "Plexiglass", "Specchi", "Materiali sintetici",
  "Legno", "Ceramica", "Cemento", "Carta/Cartone", "Tessuto",
  "Superfici verniciate",
];

function DataEditModal({ product, onClose, onSaved }) {
  const initialAdmin = (product.materials_admin && product.materials_admin.length > 0)
    ? product.materials_admin
    : (product.materials || []); // pre-popola dai PDF se admin vuoto
  const [materials, setMaterials] = useState(initialAdmin);
  const [newMaterial, setNewMaterial] = useState("");
  const [newMaterialCanonical, setNewMaterialCanonical] = useState(null); // {input, canonical, changed}
  const [cleaning, setCleaning] = useState(false);
  const [tempMin, setTempMin] = useState(
    product.temperature_min_admin != null ? String(product.temperature_min_admin)
      : (product.temperature_min != null ? String(product.temperature_min) : "")
  );
  const [tempMax, setTempMax] = useState(
    product.temperature_max_admin != null ? String(product.temperature_max_admin)
      : (product.temperature_max != null ? String(product.temperature_max) : "")
  );
  const [stressList, setStressList] = useState(product.stress_admin || (product.stress ? [product.stress] : []));
  const [resistanceMap, setResistanceMap] = useState({
    ...(product.resistance || {}),
    ...(product.resistance_admin || {}),
  });
  const [formList, setFormList] = useState(product.form_admin || (product.form ? [product.form] : []));
  const [protectionList, setProtectionList] = useState(product.protection_type_admin || (product.protection_type ? [product.protection_type] : []));
  const [treatmentDuration, setTreatmentDuration] = useState(product.treatment_duration_admin || null);
  const [colors, setColors] = useState(product.colors_admin || (product.color ? [product.color] : []));
  const [newColor, setNewColor] = useState("");
  const [preferredKws, setPreferredKws] = useState(product.preferred_keywords || []);
  const [preferredBoost, setPreferredBoost] = useState(String(product.preferred_boost ?? 12));
  const [excludeKws, setExcludeKws] = useState(product.exclude_keywords || []);
  const [incompatMats, setIncompatMats] = useState(product.incompatible_materials || []);
  const [newPreferred, setNewPreferred] = useState("");
  const [newExclude, setNewExclude] = useState("");
  const [newIncompat, setNewIncompat] = useState("");
  const [searchAliases, setSearchAliases] = useState(product.search_aliases || []);
  const [newSearchAlias, setNewSearchAlias] = useState("");
  const [wizardOpts, setWizardOpts] = useState({ stress_incollare: [], stress_lubrificare: [], resistance: [], form: [], protection_type: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/wizard-options")
      .then((r) => setWizardOpts(r.data))
      .catch(() => {});
  }, []);

  // Debounced live preview: canonicalizzazione del "nuovo materiale" mentre l'admin digita
  useEffect(() => {
    const v = newMaterial.trim();
    if (!v) {
      setNewMaterialCanonical(null);
      return;
    }
    const timer = setTimeout(() => {
      api.get(`/material-aliases/preview?text=${encodeURIComponent(v)}`)
        .then((r) => setNewMaterialCanonical(r.data))
        .catch(() => setNewMaterialCanonical(null));
    }, 250);
    return () => clearTimeout(timer);
  }, [newMaterial]);

  const cats = (product.categories && product.categories.length > 0)
    ? product.categories
    : (product.category ? [product.category] : []);
  const showStress = cats.includes("incollare") || cats.includes("lubrificare");
  const showResistance = cats.includes("incollare") || cats.includes("lubrificare") || cats.includes("protettivi");
  const showForm = cats.includes("pulire") || cats.includes("protettivi") || cats.includes("incollare");
  const showProtection = cats.includes("protettivi");
  const showColors = cats.includes("incollare");

  const addMaterial = (m) => {
    // Se l'admin ha digitato un valore che verrà canonicalizzato, usiamo la forma canonica
    let val = (m || newMaterial).trim();
    if (!m && newMaterialCanonical && newMaterialCanonical.canonical) {
      val = newMaterialCanonical.canonical;
    }
    if (!val) return;
    if (materials.some((x) => x.toLowerCase() === val.toLowerCase())) return;
    setMaterials([...materials, val]);
    setNewMaterial("");
    setNewMaterialCanonical(null);
  };
  const removeMaterial = (i) => setMaterials(materials.filter((_, idx) => idx !== i));

  const cleanupMaterials = async () => {
    if (!product?.id) return;
    setCleaning(true);
    try {
      const r = await api.post(`/products/${product.id}/canonicalize-materials`);
      const { before, after } = r.data;
      setMaterials(after);
      const removed = before.length - after.length;
      if (removed > 0) toast.success(`Ripuliti ${removed} duplicati / voci non canoniche`);
      else if (JSON.stringify(before) !== JSON.stringify(after)) toast.success("Materiali normalizzati");
      else toast.info("Nessuna modifica necessaria");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore ripulitura");
    } finally {
      setCleaning(false);
    }
  };

  const toggleInList = (list, setter, val) => {
    if (list.includes(val)) setter(list.filter((x) => x !== val));
    else setter([...list, val]);
  };
  const toggleResistance = (key) => {
    setResistanceMap({ ...resistanceMap, [key]: !resistanceMap[key] });
  };

  const addPreferred = () => {
    const v = newPreferred.trim().toLowerCase();
    if (!v) return;
    if (preferredKws.some((x) => x.toLowerCase() === v)) return;
    setPreferredKws([...preferredKws, v]);
    setNewPreferred("");
  };
  const removePreferred = (i) => setPreferredKws(preferredKws.filter((_, idx) => idx !== i));

  const addExclude = () => {
    const v = newExclude.trim().toLowerCase();
    if (!v) return;
    if (excludeKws.some((x) => x.toLowerCase() === v)) return;
    setExcludeKws([...excludeKws, v]);
    setNewExclude("");
  };
  const removeExclude = (i) => setExcludeKws(excludeKws.filter((_, idx) => idx !== i));

  const addIncompat = (val) => {
    const v = (val || newIncompat).trim().toLowerCase();
    if (!v) return;
    if (incompatMats.some((x) => x.toLowerCase() === v)) return;
    setIncompatMats([...incompatMats, v]);
    setNewIncompat("");
  };
  const removeIncompat = (i) => setIncompatMats(incompatMats.filter((_, idx) => idx !== i));

  const addColor = (c) => {
    const val = (c || newColor).trim();
    if (!val) return;
    if (colors.some((x) => x.toLowerCase() === val.toLowerCase())) return;
    setColors([...colors, val]);
    setNewColor("");
  };
  const removeColor = (i) => setColors(colors.filter((_, idx) => idx !== i));

  const addSearchAlias = () => {
    const v = newSearchAlias.trim();
    if (!v) return;
    if (searchAliases.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    setSearchAliases([...searchAliases, v]);
    setNewSearchAlias("");
  };
  const removeSearchAlias = (i) => setSearchAliases(searchAliases.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const parseNum = (v) => {
        const s = String(v).trim();
        if (s === "") return null;
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : null;
      };
      // Solo le flag true finiscono in resistance_admin
      const resAdmin = Object.fromEntries(
        Object.entries(resistanceMap).filter(([, v]) => v === true)
      );
      await api.patch(`/products/${product.id}/data`, {
        materials_admin: materials,
        temperature_min_admin: parseNum(tempMin),
        temperature_max_admin: parseNum(tempMax),
        stress_admin: stressList,
        resistance_admin: resAdmin,
        form_admin: formList,
        protection_type_admin: protectionList,
        preferred_keywords: preferredKws,
        preferred_boost: parseNum(preferredBoost) ?? 12,
        exclude_keywords: excludeKws,
        incompatible_materials: incompatMats,
        treatment_duration_admin: treatmentDuration,
        colors_admin: colors,
        search_aliases: searchAliases,
      });
      toast.success("Dati tecnici aggiornati");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento dati");
    } finally {
      setSaving(false);
    }
  };

  const chipCls = (active) => `text-xs px-3 py-1.5 border transition-colors ${active
    ? "bg-orange-500/20 border-orange-500 text-orange-300"
    : "border-slate-700 hover:border-orange-800 hover:text-orange-400 text-slate-400"}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="data-modal">
      <div className="tm-card p-8 w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="tm-tag inline-block mb-4">Modifica dati tecnici</div>
        <h3 className="font-heading text-2xl text-white tracking-tighter mb-2">
          {product.name}
        </h3>
        <p className="text-slate-400 text-sm mb-6">
          I dati inseriti qui hanno <span className="text-orange-400 font-semibold">priorità sul PDF</span> durante la ricerca.
          Se lasci vuoto, verranno usati i valori estratti dalla scheda tecnica.
        </p>

        <form onSubmit={submit} className="space-y-6">
          {/* MATERIALI */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="tm-label mb-0">Materiali compatibili</label>
              <button
                type="button"
                onClick={cleanupMaterials}
                disabled={cleaning || materials.length === 0}
                className="text-xs px-3 py-1.5 border border-orange-800 hover:border-orange-500 text-orange-400 font-mono uppercase tracking-widest disabled:opacity-40 transition-colors"
                title="Applica canonicalizzazione (typos, plurali, abbreviazioni) e rimuove duplicati"
                data-testid="cleanup-materials-btn"
              >
                {cleaning ? "Pulisco..." : "🧹 Ripulisci"}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {materials.length === 0 && (
                <span className="text-slate-500 text-sm italic">Nessun materiale aggiunto</span>
              )}
              {materials.map((m, i) => (
                <span key={`${m}-${i}`} className="tm-tag text-orange-400 border-orange-800 flex items-center gap-2" data-testid={`material-tag-${i}`}>
                  {m}
                  <button type="button" onClick={() => removeMaterial(i)} className="text-red-400 hover:text-red-300" aria-label={`Rimuovi ${m}`} data-testid={`remove-material-${i}`}>×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                className="tm-input flex-1"
                placeholder="Aggiungi materiale (es. Acciaio, PE, plexiglas)"
                value={newMaterial}
                onChange={(e) => setNewMaterial(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMaterial(); } }}
                data-testid="new-material-input"
              />
              <button type="button" onClick={() => addMaterial()} className="tm-btn-outline px-4" data-testid="add-material-btn">Aggiungi</button>
            </div>
            {newMaterialCanonical && newMaterialCanonical.changed && (
              <div className="mt-2 text-xs font-mono text-orange-400" data-testid="canonical-suggestion">
                💡 Verrà salvato come <span className="font-semibold">&ldquo;{newMaterialCanonical.canonical}&rdquo;</span> (forma canonica)
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-1">
              <span className="text-xs text-slate-500 font-mono mr-2 self-center">Suggerimenti:</span>
              {COMMON_MATERIALS.filter((cm) => !materials.some((m) => m.toLowerCase() === cm.toLowerCase())).slice(0, 12).map((cm) => (
                <button key={cm} type="button" onClick={() => addMaterial(cm)} className="text-xs px-2 py-1 border border-slate-700 hover:border-orange-800 hover:text-orange-400 text-slate-400 transition-colors" data-testid={`suggest-material-${cm}`}>+ {cm}</button>
              ))}
            </div>
          </div>

          {/* MATERIALI NON COMPATIBILI (esclusione hard) */}
          <div>
            <label className="tm-label">🚫 Materiali NON compatibili</label>
            <p className="text-xs text-slate-400 mb-2">
              Se il cliente menziona uno di questi materiali nella descrizione libera, il prodotto viene <span className="text-red-400 font-semibold">escluso automaticamente</span> dai risultati.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {incompatMats.length === 0 && (
                <span className="text-slate-500 text-xs italic">Nessun materiale incompatibile</span>
              )}
              {incompatMats.map((m, i) => (
                <span key={`inc-${m}-${i}`} className="tm-tag text-red-400 border-red-800 flex items-center gap-2" data-testid={`incompat-tag-${i}`}>
                  {m}
                  <button type="button" onClick={() => removeIncompat(i)} className="text-red-400 hover:text-red-300" aria-label={`Rimuovi ${m}`} data-testid={`remove-incompat-${i}`}>×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                className="tm-input flex-1"
                placeholder="Aggiungi materiale incompatibile (es. specchi)"
                value={newIncompat}
                onChange={(e) => setNewIncompat(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIncompat(); } }}
                data-testid="new-incompat-input"
              />
              <button type="button" onClick={() => addIncompat()} className="tm-btn-outline px-4" data-testid="add-incompat-btn">Aggiungi</button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              <span className="text-xs text-slate-500 font-mono mr-2 self-center">Suggerimenti:</span>
              {COMMON_MATERIALS.filter((cm) => !incompatMats.some((m) => m.toLowerCase() === cm.toLowerCase())).slice(0, 10).map((cm) => (
                <button key={cm} type="button" onClick={() => addIncompat(cm)} className="text-xs px-2 py-1 border border-red-900 hover:border-red-500 hover:text-red-400 text-slate-400 transition-colors" data-testid={`suggest-incompat-${cm}`}>+ {cm}</button>
              ))}
            </div>
          </div>

          {/* TEMPERATURA */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="tm-label">Temperatura min (°C)</label>
              <input type="number" className="tm-input" placeholder="es. -40" value={tempMin} onChange={(e) => setTempMin(e.target.value)} data-testid="temp-min-input" />
            </div>
            <div>
              <label className="tm-label">Temperatura max (°C)</label>
              <input type="number" className="tm-input" placeholder="es. 200" value={tempMax} onChange={(e) => setTempMax(e.target.value)} data-testid="temp-max-input" />
            </div>
          </div>

          {/* SOLLECITAZIONI / MOVIMENTO — opzioni diverse per Incolla vs Lubrifica */}
          {showStress && (() => {
            const stressOptions = [];
            const seen = new Set();
            const addAll = (list) => {
              for (const o of (list || [])) {
                if (!seen.has(o.value)) { seen.add(o.value); stressOptions.push(o); }
              }
            };
            if (cats.includes("incollare")) addAll(wizardOpts.stress_incollare);
            if (cats.includes("lubrificare")) addAll(wizardOpts.stress_lubrificare);
            const bothCats = cats.includes("incollare") && cats.includes("lubrificare");
            const stressLabel = bothCats
              ? "Sollecitazione / movimento supportato"
              : cats.includes("lubrificare") ? "Tipo di movimento supportato" : "Tipo di sollecitazione supportata";
            return (
              <div>
                <label className="tm-label">{stressLabel}</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {stressOptions.map((o) => (
                    <button key={o.value} type="button" onClick={() => toggleInList(stressList, setStressList, o.value)} className={chipCls(stressList.includes(o.value))} data-testid={`stress-chip-${o.value}`}>
                      {stressList.includes(o.value) ? "✓ " : ""}{o.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* RESISTENZE */}
          {showResistance && (
            <div>
              <label className="tm-label">Resistenze del prodotto</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {wizardOpts.resistance.map((o) => (
                  <button key={o.value} type="button" onClick={() => toggleResistance(o.value)} className={chipCls(!!resistanceMap[o.value])} data-testid={`resistance-chip-${o.value}`}>
                    {resistanceMap[o.value] ? "✓ " : ""}{o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* FORMA */}
          {showForm && (
            <div>
              <label className="tm-label">Forma del prodotto</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {wizardOpts.form.map((o) => (
                  <button key={o.value} type="button" onClick={() => toggleInList(formList, setFormList, o.value)} className={chipCls(formList.includes(o.value))} data-testid={`form-chip-${o.value}`}>
                    {formList.includes(o.value) ? "✓ " : ""}{o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* TIPO PROTEZIONE */}
          {showProtection && (
            <div>
              <label className="tm-label">Tipo di protezione</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {wizardOpts.protection_type.map((o) => (
                  <button key={o.value} type="button" onClick={() => toggleInList(protectionList, setProtectionList, o.value)} className={chipCls(protectionList.includes(o.value))} data-testid={`protection-chip-${o.value}`}>
                    {protectionList.includes(o.value) ? "✓ " : ""}{o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* TRATTAMENTO (temporaneo vs definitivo) — solo protettivi */}
          {showProtection && (
            <div>
              <label className="tm-label">Tipo di trattamento</label>
              <p className="text-xs text-slate-400 mb-2">
                Temporaneo/Removibile = si può togliere con solventi. Definitivo = permanente.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: null, label: "— non specificato —" },
                  { value: "temporary", label: "Temporaneo / Removibile" },
                  { value: "permanent", label: "Definitivo" },
                ].map((o) => (
                  <button
                    key={String(o.value)}
                    type="button"
                    onClick={() => setTreatmentDuration(o.value)}
                    className={chipCls(treatmentDuration === o.value)}
                    data-testid={`treatment-chip-${o.value || "none"}`}
                  >
                    {treatmentDuration === o.value ? "✓ " : ""}{o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* COLORI DISPONIBILI (solo per Incolla/Sigilla) */}
          {showColors && (
            <div>
              <label className="tm-label">🎨 Colori disponibili</label>
              <p className="text-xs text-slate-400 mb-2">
                Elenca i colori in cui il prodotto è disponibile (es. Bianco, Nero, Trasparente, Grigio). Solo informativo.
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {colors.length === 0 && (
                  <span className="text-slate-500 text-xs italic">Nessun colore aggiunto</span>
                )}
                {colors.map((c, i) => (
                  <span key={`col-${c}-${i}`} className="tm-tag text-cyan-400 border-cyan-800 flex items-center gap-2" data-testid={`color-tag-${i}`}>
                    {c}
                    <button type="button" onClick={() => removeColor(i)} className="text-red-400 hover:text-red-300" aria-label={`Rimuovi ${c}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="tm-input flex-1"
                  placeholder="Aggiungi colore (es. Bianco)"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addColor(); } }}
                  data-testid="new-color-input"
                />
                <button type="button" onClick={() => addColor()} className="tm-btn-outline px-4" data-testid="add-color-btn">Aggiungi</button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                <span className="text-xs text-slate-500 font-mono mr-2 self-center">Suggerimenti:</span>
                {["Bianco","Nero","Trasparente","Grigio","Rosso","Blu","Marrone","Beige","Argento","Nichelato"].filter((cc) => !colors.some((c) => c.toLowerCase() === cc.toLowerCase())).slice(0, 10).map((cc) => (
                  <button key={cc} type="button" onClick={() => addColor(cc)} className="text-xs px-2 py-1 border border-slate-700 hover:border-cyan-800 hover:text-cyan-400 text-slate-400 transition-colors" data-testid={`suggest-color-${cc}`}>+ {cc}</button>
                ))}
              </div>
            </div>
          )}

          {/* PREFERRED / EXCLUDE KEYWORDS — interpretazione della descrizione libera */}
          <div className="border border-orange-800/40 bg-orange-500/5 p-4 space-y-4">
            <div>
              <label className="tm-label">🎯 Favorisci quando il cliente scrive…</label>
              <p className="text-xs text-slate-400 mb-2">
                Parole o frasi (es. <span className="font-mono text-orange-400">&quot;cavi&quot;</span>, <span className="font-mono text-orange-400">&quot;sigillare cavi&quot;</span>) che, se presenti nella descrizione libera del cliente, spingono questo prodotto in cima.
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {preferredKws.length === 0 && (
                  <span className="text-slate-500 text-xs italic">Nessuna parola-chiave</span>
                )}
                {preferredKws.map((k, i) => (
                  <span key={`pk-${k}-${i}`} className="tm-tag text-green-400 border-green-800 flex items-center gap-2" data-testid={`pref-kw-${i}`}>
                    {k}
                    <button type="button" onClick={() => removePreferred(i)} className="text-red-400 hover:text-red-300" aria-label={`Rimuovi ${k}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <input
                  type="text"
                  className="tm-input flex-1 min-w-[200px]"
                  placeholder="es. cavi, sigillare cavi, isolare cavi"
                  value={newPreferred}
                  onChange={(e) => setNewPreferred(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPreferred(); } }}
                  data-testid="new-preferred-input"
                />
                <button type="button" onClick={addPreferred} className="tm-btn-outline px-4" data-testid="add-preferred-btn">Aggiungi</button>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-400 font-mono uppercase">Boost:</label>
                  <input
                    type="number"
                    step="1"
                    className="tm-input w-20"
                    value={preferredBoost}
                    onChange={(e) => setPreferredBoost(e.target.value)}
                    data-testid="preferred-boost-input"
                    title="Peso del boost (default 12). Valori alti danno priorità estrema."
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="tm-label">🚫 Escludi quando il cliente scrive…</label>
              <p className="text-xs text-slate-400 mb-2">
                Parole che, se presenti, <span className="text-red-400 font-semibold">nascondono</span> questo prodotto (es. <span className="font-mono text-red-400">&quot;cavi&quot;</span> su un prodotto NON adatto ai cavi).
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {excludeKws.length === 0 && (
                  <span className="text-slate-500 text-xs italic">Nessuna esclusione</span>
                )}
                {excludeKws.map((k, i) => (
                  <span key={`ex-${k}-${i}`} className="tm-tag text-red-400 border-red-800 flex items-center gap-2" data-testid={`excl-kw-${i}`}>
                    {k}
                    <button type="button" onClick={() => removeExclude(i)} className="text-red-400 hover:text-red-300" aria-label={`Rimuovi ${k}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="tm-input flex-1"
                  placeholder="es. cavi, alimentari, uso interno"
                  value={newExclude}
                  onChange={(e) => setNewExclude(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExclude(); } }}
                  data-testid="new-exclude-input"
                />
                <button type="button" onClick={addExclude} className="tm-btn-outline px-4" data-testid="add-exclude-btn">Aggiungi</button>
              </div>
            </div>

            <div>
              <label className="tm-label">🔍 Alias di ricerca (nomi/sinonimi)</label>
              <p className="text-xs text-slate-400 mb-2">
                Parole o espressioni che, se digitate dal cliente nella <span className="text-orange-300">ricerca libera</span>, fanno comparire questo prodotto. <span className="font-semibold">NON appaiono nei risultati</span> (restano solo lato backend).
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {searchAliases.length === 0 && (
                  <span className="text-slate-500 text-xs italic">Nessun alias impostato</span>
                )}
                {searchAliases.map((a, i) => (
                  <span key={`alias-${a}-${i}`} className="tm-tag text-orange-300 border-orange-800 flex items-center gap-2" data-testid={`search-alias-${i}`}>
                    {a}
                    <button type="button" onClick={() => removeSearchAlias(i)} className="text-orange-300 hover:text-orange-200" aria-label={`Rimuovi ${a}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="tm-input flex-1"
                  placeholder="es. sgorgatore, sturalavandini, collante universale"
                  value={newSearchAlias}
                  onChange={(e) => setNewSearchAlias(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSearchAlias(); } }}
                  data-testid="new-search-alias-input"
                />
                <button type="button" onClick={addSearchAlias} className="tm-btn-outline px-4" data-testid="add-search-alias-btn">Aggiungi</button>
              </div>
            </div>
          </div>

          {/* PDF INFO */}
          <div className="text-xs text-slate-500 font-mono border-t border-slate-800 pt-3 space-y-1">
            <div>PDF — Materiali: {(product.materials || []).join(", ") || "—"}</div>
            <div>PDF — Temperatura: {product.temperature_min ?? "?"} / {product.temperature_max ?? "?"} °C</div>
            {product.stress && <div>PDF — Sollecitazione: {product.stress}</div>}
            {product.form && <div>PDF — Forma: {product.form}</div>}
            {product.protection_type && <div>PDF — Protezione: {product.protection_type}</div>}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="tm-btn-outline flex-1" data-testid="data-cancel">Annulla</button>
            <button type="submit" disabled={saving} className="tm-btn-primary flex-1 disabled:opacity-50" data-testid="data-save">
              {saving ? "Salvo..." : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


const WIZARD_KEYS = [
  { key: "stress_incollare", label: "Sollecitazione (Incolla)", hint: "Statica, vibrazioni, trazione, taglio, strappo, torsione (categoria: Incolla)" },
  { key: "stress_lubrificare", label: "Movimento (Lubrifica)", hint: "Statico, dinamico, scorrimento, alta pressione, catene/funi (categoria: Lubrifica)" },
  { key: "resistance", label: "Resistenze", hint: "Acqua, oli, UV, chimici... (categorie: Incolla, Lubrifica, Proteggi)" },
  { key: "form", label: "Forma prodotto", hint: "Liquida, aerosol, gel, guaina, nastro... (categorie: Pulisci, Proteggi)" },
  { key: "protection_type", label: "Tipo di protezione", hint: "Anticorrosivo, idrorepellente, cera... (categoria: Proteggi)" },
  { key: "_materials_aliases", label: "Sinonimi materiali", hint: "Definisci alias custom (es. 'poliuretano espanso' → 'Poliuretano')" },
];

function WizardOptionsEditor() {
  const [opts, setOpts] = useState({ stress_incollare: [], stress_lubrificare: [], resistance: [], form: [], protection_type: [] });
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState("stress_incollare");

  const load = () => {
    setLoading(true);
    api.get("/wizard-options")
      .then((r) => setOpts(r.data))
      .catch(() => toast.error("Errore caricamento opzioni"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const save = async (key, newOptions) => {
    try {
      const r = await api.put(`/wizard-options/${key}`, { options: newOptions });
      setOpts({ ...opts, [key]: r.data.options });
      toast.success("Opzioni aggiornate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const meta = WIZARD_KEYS.find((k) => k.key === activeKey);
  const list = opts[activeKey] || [];

  return (
    <div className="tm-card p-6">
      <div className="mb-6">
        <h2 className="font-heading text-2xl text-white tracking-tighter mb-2">Opzioni del Wizard cliente</h2>
        <p className="text-slate-400 text-sm">
          Modifica le opzioni mostrate al cliente nelle domande del wizard <span className="text-orange-400 font-semibold">&ldquo;trova il prodotto in 60 secondi&rdquo;</span>.
          Le modifiche sono visibili subito ai nuovi utenti.
        </p>
      </div>

      <div className="flex gap-2 border-b border-slate-700 mb-6 flex-wrap">
        {WIZARD_KEYS.map((k) => (
          <button
            key={k.key}
            onClick={() => setActiveKey(k.key)}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border-b-2 transition-colors ${
              activeKey === k.key ? "border-orange-500 text-orange-500" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
            data-testid={`wizard-tab-${k.key}`}
          >
            {k.label} <span className="ml-1 text-slate-500">({(opts[k.key] || []).length})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500 font-mono uppercase tracking-widest text-sm">Caricamento...</div>
      ) : activeKey === "_materials_aliases" ? (
        <MaterialAliasesEditor />
      ) : (
        <OptionListEditor
          key={activeKey}
          meta={meta}
          options={list}
          onSave={(newOptions) => save(activeKey, newOptions)}
        />
      )}
    </div>
  );
}

function MaterialAliasesEditor() {
  const [data, setData] = useState({ static: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [newAlias, setNewAlias] = useState("");
  const [newCanonical, setNewCanonical] = useState("");
  const [staticFilter, setStaticFilter] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/material-aliases")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore caricamento alias"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const a = newAlias.trim().toLowerCase();
    const c = newCanonical.trim();
    if (!a || !c) {
      toast.error("Inserisci sia l'alias sia la forma canonica");
      return;
    }
    try {
      await api.post("/material-aliases", { alias: a, canonical: c });
      toast.success("Alias aggiunto");
      setNewAlias("");
      setNewCanonical("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiunta alias");
    }
  };

  const remove = async (alias) => {
    try {
      await api.delete(`/material-aliases/${encodeURIComponent(alias)}`);
      toast.success("Alias rimosso");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore rimozione alias");
    }
  };

  const filteredStatic = data.static.filter((s) =>
    !staticFilter ||
    s.alias.toLowerCase().includes(staticFilter.toLowerCase()) ||
    s.canonical.toLowerCase().includes(staticFilter.toLowerCase())
  );

  if (loading) return <div className="p-8 text-center text-slate-500 font-mono uppercase tracking-widest text-sm">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <p className="text-slate-400 text-sm">
        Gli alias controllano la <span className="text-orange-400 font-semibold">canonicalizzazione automatica</span> dei materiali:
        errori di battitura, abbreviazioni (PE ↔ Polietilene), singolare/plurale (metallo ↔ metalli).
        I nuovi alias custom si applicano subito a tutta la piattaforma (wizard cliente + admin).
      </p>

      {/* AGGIUNGI NUOVO ALIAS */}
      <div className="tm-card p-5 border-orange-800/40 bg-orange-500/5">
        <div className="tm-label mb-3">Aggiungi nuovo alias</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-slate-400 mb-1 font-mono">Alias (input libero)</div>
            <input
              type="text"
              className="tm-input"
              placeholder="es. poliuretano espanso"
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              data-testid="new-alias-input"
            />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1 font-mono">→ Canonica (visibile al cliente)</div>
            <input
              type="text"
              className="tm-input"
              placeholder="es. Poliuretano"
              value={newCanonical}
              onChange={(e) => setNewCanonical(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              data-testid="new-canonical-input"
            />
          </div>
        </div>
        <button type="button" onClick={add} className="tm-btn-primary mt-3 text-sm" data-testid="add-alias-btn">
          + Aggiungi alias
        </button>
      </div>

      {/* ALIAS CUSTOM (modificabili) */}
      <div>
        <div className="tm-label mb-2">Alias personalizzati ({data.custom.length})</div>
        {data.custom.length === 0 ? (
          <div className="text-slate-500 italic text-sm p-3 border border-slate-800">
            Nessun alias custom. Aggiungine uno qui sopra.
          </div>
        ) : (
          <div className="space-y-2">
            {data.custom.map((a) => (
              <div key={a.alias} className="flex items-center gap-3 border border-slate-700 bg-slate-900/50 p-2" data-testid={`custom-alias-row-${a.alias}`}>
                <div className="font-mono text-xs text-slate-400 flex-1">
                  <span className="text-orange-400">{a.alias}</span>
                  <span className="mx-2 text-slate-600">→</span>
                  <span className="text-green-400">{a.canonical}</span>
                </div>
                <button type="button" onClick={() => remove(a.alias)} className="text-red-400 hover:text-red-300 px-3 font-mono text-xs uppercase tracking-widest" data-testid={`remove-alias-${a.alias}`}>
                  Elimina
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ALIAS STATICI (readonly) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="tm-label mb-0">Alias di sistema ({filteredStatic.length}/{data.static.length}) — readonly</div>
          <input
            type="text"
            className="tm-input w-56 text-sm"
            placeholder="Filtra..."
            value={staticFilter}
            onChange={(e) => setStaticFilter(e.target.value)}
            data-testid="static-alias-filter"
          />
        </div>
        <div className="max-h-96 overflow-y-auto border border-slate-800 divide-y divide-slate-800/60">
          {filteredStatic.map((a) => (
            <div key={a.alias} className="flex items-center gap-3 px-3 py-1.5 hover:bg-slate-900/50">
              <div className="font-mono text-xs text-slate-500 flex-1">
                <span className="text-slate-300">{a.alias}</span>
                <span className="mx-2 text-slate-700">→</span>
                <span className="text-slate-400">{a.canonical}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OptionListEditor({ meta, options, onSave }) {
  const [local, setLocal] = useState(options);
  const [newValue, setNewValue] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal(options); }, [options]);

  const add = () => {
    const v = newValue.trim().toLowerCase();
    const lb = newLabel.trim() || v;
    if (!v) {
      toast.error("Valore (value) obbligatorio");
      return;
    }
    if (local.some((o) => o.value.toLowerCase() === v)) {
      toast.error("Questo valore esiste già");
      return;
    }
    setLocal([...local, { value: v, label: lb }]);
    setNewValue("");
    setNewLabel("");
  };

  const remove = (i) => setLocal(local.filter((_, idx) => idx !== i));

  const updateLabel = (i, lb) => {
    const copy = [...local];
    copy[i] = { ...copy[i], label: lb };
    setLocal(copy);
  };

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= local.length) return;
    const copy = [...local];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setLocal(copy);
  };

  const persist = async () => {
    setSaving(true);
    try {
      await onSave(local);
    } finally {
      setSaving(false);
    }
  };

  const isDirty = JSON.stringify(local) !== JSON.stringify(options);

  return (
    <div className="space-y-4">
      <p className="text-slate-500 text-xs font-mono">{meta?.hint}</p>

      <div className="space-y-2" data-testid="option-list">
        {local.length === 0 && (
          <div className="p-4 text-slate-500 italic text-sm">Nessuna opzione. Aggiungine una qui sotto.</div>
        )}
        {local.map((o, i) => (
          <div key={`${o.value}-${i}`} className="flex items-center gap-2 border border-slate-700 bg-slate-900/50 p-2" data-testid={`option-row-${o.value}`}>
            <div className="flex flex-col">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-500 hover:text-orange-400 text-xs disabled:opacity-30" aria-label="Su">▲</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === local.length - 1} className="text-slate-500 hover:text-orange-400 text-xs disabled:opacity-30" aria-label="Giù">▼</button>
            </div>
            <div className="font-mono text-xs text-orange-500 min-w-[110px] uppercase">{o.value}</div>
            <input
              type="text"
              value={o.label}
              onChange={(e) => updateLabel(i, e.target.value)}
              className="tm-input flex-1"
              placeholder="Etichetta visibile al cliente"
              data-testid={`option-label-${o.value}`}
            />
            <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-300 px-3 font-mono text-xs uppercase tracking-widest" data-testid={`option-remove-${o.value}`}>
              Elimina
            </button>
          </div>
        ))}
      </div>

      <div className="border border-dashed border-slate-700 p-4 space-y-3">
        <div className="tm-label">Aggiungi nuova opzione</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            type="text"
            className="tm-input"
            placeholder="Valore tecnico (es. poliuretano)"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            data-testid="new-option-value"
          />
          <input
            type="text"
            className="tm-input"
            placeholder="Etichetta visibile (es. Poliuretano)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            data-testid="new-option-label"
          />
        </div>
        <button type="button" onClick={add} className="tm-btn-outline text-sm" data-testid="add-option-btn">
          + Aggiungi
        </button>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
        <button
          type="button"
          onClick={() => setLocal(options)}
          disabled={!isDirty}
          className="tm-btn-outline disabled:opacity-40"
          data-testid="wizard-reset-btn"
        >
          Annulla modifiche
        </button>
        <button
          type="button"
          onClick={persist}
          disabled={!isDirty || saving}
          className="tm-btn-primary disabled:opacity-40"
          data-testid="wizard-save-btn"
        >
          {saving ? "Salvo..." : "Salva modifiche"}
        </button>
      </div>
    </div>
  );
}


function AccessRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(null); // request id being processed

  const load = () => {
    setLoading(true);
    api.get("/access-requests")
      .then((r) => setRequests(r.data))
      .catch(() => toast.error("Errore caricamento richieste accesso"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const provision = async (r) => {
    if (!window.confirm(`Generare credenziali cliente per ${r.email} e aprire l'email pre-compilata?\n\nSe l'utente esiste già la password verrà resettata.`)) return;
    setProvisioning(r.id);
    try {
      const res = await api.post(`/access-requests/${r.id}/provision`);
      const { email, password, name, reused_existing_user } = res.data;
      const subject = `Le tue credenziali TOP-TEC Product Finder`;
      const body = [
        `Ciao ${name || ""},`,
        ``,
        `${reused_existing_user ? "abbiamo aggiornato la tua password" : "abbiamo creato il tuo account cliente"} per la piattaforma TOP-TEC Product Finder.`,
        ``,
        `Ecco le tue credenziali di accesso:`,
        ``,
        `  Email:    ${email}`,
        `  Password: ${password}`,
        ``,
        `Link di accesso: www.top-tec.it`,
        ``,
        `Per qualsiasi domanda, rispondi pure a questa email.`,
      ].join("\n");
      const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      toast.success("Credenziali generate. Apri la tua app mail per inviare.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore generazione credenziali");
    } finally {
      setProvisioning(null);
    }
  };

  const toggleStatus = async (r) => {
    const newStatus = r.status === "pending" ? "processed" : "pending";
    try {
      await api.patch(`/access-requests/${r.id}?status=${newStatus}`);
      toast.success(newStatus === "processed" ? "Segnata come inviata" : "Riaperta");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la richiesta di ${r.name}?`)) return;
    try {
      await api.delete(`/access-requests/${r.id}`);
      toast.success("Richiesta eliminata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const pending = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="tm-card overflow-hidden">
      <div className="bg-slate-800/80 border-b border-slate-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="tm-tag text-orange-500 border-orange-800">🔑 Richieste credenziali</span>
          {pending > 0 && (
            <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold" data-testid="access-pending-badge">
              {pending}
            </span>
          )}
        </div>
        <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">
          {requests.length} totali
        </span>
      </div>
      {loading ? (
        <div className="p-8 text-center text-slate-500 font-mono uppercase tracking-widest text-sm">Caricamento...</div>
      ) : requests.length === 0 ? (
        <div className="p-8 text-center text-slate-400" data-testid="access-empty">
          Nessuna richiesta di accesso al momento.
        </div>
      ) : (
        <table className="w-full">
          <thead className="bg-slate-800/40 border-b border-slate-700">
            <tr className="text-left font-mono text-xs uppercase tracking-widest text-slate-400">
              <th className="p-4">Stato</th>
              <th className="p-4">Data</th>
              <th className="p-4">Richiedente</th>
              <th className="p-4 hidden md:table-cell">Azienda / Tel.</th>
              <th className="p-4 hidden lg:table-cell">Note</th>
              <th className="p-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className={`border-b border-slate-800 hover:bg-slate-800/40 ${r.status === "pending" ? "bg-orange-500/5" : "opacity-60"}`} data-testid={`access-row-${r.id}`}>
                <td className="p-4">
                  {r.status === "pending" ? (
                    <span className="tm-tag text-orange-500 border-orange-800">● In attesa</span>
                  ) : (
                    <span className="tm-tag text-green-500 border-green-800">✓ Inviata</span>
                  )}
                </td>
                <td className="p-4 text-slate-400 font-mono text-xs">
                  {new Date(r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </td>
                <td className="p-4">
                  <div className="text-white font-medium">{r.name}</div>
                  <a href={`mailto:${r.email}?subject=Credenziali TOP-TEC Product Finder`} className="text-orange-400 hover:text-orange-300 text-xs" data-testid={`access-email-${r.id}`}>
                    {r.email}
                  </a>
                </td>
                <td className="p-4 hidden md:table-cell text-slate-300 text-sm">
                  {r.company && <div>{r.company}</div>}
                  {r.phone && (
                    <a href={`tel:${r.phone}`} className="text-slate-500 text-xs hover:text-orange-400">
                      {r.phone}
                    </a>
                  )}
                  {!r.company && !r.phone && <span className="text-slate-600 italic">—</span>}
                </td>
                <td className="p-4 hidden lg:table-cell text-slate-300 text-sm max-w-xs">
                  {r.message || <span className="text-slate-600 italic">—</span>}
                </td>
                <td className="p-4 text-right space-x-3 whitespace-nowrap">
                  {r.status === "pending" && (
                    <button
                      onClick={() => provision(r)}
                      disabled={provisioning === r.id}
                      className="font-mono text-xs uppercase tracking-widest text-green-500 hover:text-green-400 disabled:opacity-40"
                      title="Genera password random, crea/aggiorna l'utente cliente e apre email pre-compilata"
                      data-testid={`access-provision-${r.id}`}
                    >
                      {provisioning === r.id ? "Genero..." : "✨ Genera e invia"}
                    </button>
                  )}
                  <button
                    onClick={() => toggleStatus(r)}
                    className="font-mono text-xs uppercase tracking-widest text-orange-500 hover:text-orange-400"
                    data-testid={`access-toggle-${r.id}`}
                  >
                    {r.status === "pending" ? "Segna inviata" : "Riapri"}
                  </button>
                  <button
                    onClick={() => remove(r)}
                    className="font-mono text-xs uppercase tracking-widest text-red-500 hover:text-red-400"
                    data-testid={`access-delete-${r.id}`}
                  >
                    Elimina
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

